import { Button, TextButton } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Modal, BREAKPOINT_MOBILE_MAX } from '@creditornot/cbt-modal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';
import Loading from 'core/Loading';
import { DiscountedItem, DiscountedItemCategory } from 'modules/campaigns/hooks/useDiscountedItems';

import { DiscountedItemsModalContent } from './DiscountedItemsModalContent';
import {
  DiscountedItemCategoryWithMatches,
  findCategoryItemsBySearchTerm,
  searchResultToIdsForSelectAll,
} from './utils';

type DiscountedItemsModalProps = {
  discountedItems: DiscountedItem[];
  discountedItemCategories: DiscountedItemCategory[];
  selectedItemIds: string[];
  selectedCategoryIds: string[];
  isLoadingMenuItems: boolean;
  isOpen: boolean;
  onSelectionChange: (values: { itemIds: string[]; categoryIds: string[] }) => void;
  onSubmit: () => void;
  onDismiss: () => void;
  onCancel: () => void;
};

const Footer = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  gap: 16px;

  @media (width <= ${BREAKPOINT_MOBILE_MAX}px) {
    flex-direction: column;
  }
`;

const FooterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (width <= ${BREAKPOINT_MOBILE_MAX}px) {
    justify-content: space-between;
  }
`;

const StyledButton = styled(Button)`
  @media (width <= ${BREAKPOINT_MOBILE_MAX}px) {
    flex: 1 1 50%;
  }
`;

const SelectedItemsCount = styled.p`
  ${typographyCss.Body3}
  color: ${color.textSubdued};
  margin: 0;
`;

const EmphasizedCount = styled.span`
  font-weight: 700;
`;

export const DiscountedItemsModal = ({
  discountedItems,
  discountedItemCategories,
  selectedItemIds,
  selectedCategoryIds,
  isLoadingMenuItems,
  isOpen,
  onSelectionChange,
  onSubmit,
  onDismiss,
  onCancel,
}: DiscountedItemsModalProps) => {
  const { formatMessage } = useIntl();

  const selectedItemsInCategoriesCount = useMemo(() => {
    const getSelectedCategoryItemCount = (categories: DiscountedItemCategory[], includeAll: boolean): number =>
      categories.reduce((itemCount, category) => {
        if (includeAll || selectedCategoryIds.includes(category.id)) {
          return category.itemIds.length + itemCount + getSelectedCategoryItemCount(category.childCategories, true);
        }
        return itemCount + getSelectedCategoryItemCount(category.childCategories, false);
      }, 0);
    return selectedCategoryIds.length ? getSelectedCategoryItemCount(discountedItemCategories, false) : 0;
  }, [discountedItemCategories, selectedCategoryIds]);
  const selectedItemsCount = selectedItemsInCategoriesCount + selectedItemIds.length;

  const totalItemsCount = useMemo(() => {
    const getCategoryItemCount = (categories: DiscountedItemCategory[]): number =>
      categories.reduce(
        (itemCount, category) => category.itemIds.length + itemCount + getCategoryItemCount(category.childCategories),
        0,
      );
    return getCategoryItemCount(discountedItemCategories);
  }, [discountedItemCategories]);

  const [filteredCategories, setFilteredCategories] = useState<DiscountedItemCategoryWithMatches[]>();

  const onFilterChange = useCallback(
    (searchStr: string, additionalCategoryIds: string[]) => {
      if (!searchStr) {
        setFilteredCategories(undefined);
      } else {
        const newFilteredCategories = findCategoryItemsBySearchTerm(
          discountedItems,
          discountedItemCategories,
          searchStr,
          additionalCategoryIds,
        );
        setFilteredCategories(newFilteredCategories);
      }
    },
    [discountedItemCategories, discountedItems],
  );

  // Make sure the filtered state is reset when the modal is closed as the content state will reset too
  useEffect(() => {
    if (!isOpen) {
      setFilteredCategories(undefined);
    }
  }, [isOpen]);

  const categoriesToDisplay = filteredCategories ?? discountedItemCategories;

  return (
    <Modal
      open={isOpen}
      onDismiss={onDismiss}
      title={formatMessage({ id: 'campaigns.form.item.select-items-or-categories' })}
      sizeConfig={{
        desktop: {
          type: 'centered',
          width: '100%',
          maxWidth: '800px',
          height: '100%',
          maxHeight: '700px',
        },
        mobile: { type: 'fullScreen' },
      }}
      disableContentPadding
      footer={
        <Footer>
          <FooterGroup>
            {!!selectedItemsCount ? (
              <>
                <TextButton onClick={() => onSelectionChange({ itemIds: [], categoryIds: [] })}>
                  <FormattedMessage id="campaigns.form.venues.deselect-all" />
                </TextButton>
                <SelectedItemsCount>
                  <FormattedMessage
                    id="campaigns.form.item.selected-items-count"
                    values={{
                      selectedItems: <EmphasizedCount>{selectedItemsCount}</EmphasizedCount>,
                      totalItems: totalItemsCount,
                    }}
                  />
                </SelectedItemsCount>
              </>
            ) : (
              <TextButton onClick={() => onSelectionChange(searchResultToIdsForSelectAll(categoriesToDisplay))}>
                <FormattedMessage id="campaigns.form.venues.select-all" />
              </TextButton>
            )}
          </FooterGroup>
          <FooterGroup>
            <StyledButton variant="secondary" size="small" onClick={onCancel}>
              <FormattedMessage id="state.cancel" />
            </StyledButton>
            <StyledButton
              type="submit"
              variant="primary"
              size="small"
              disabled={!selectedItemsCount}
              onClick={onSubmit}
            >
              <FormattedMessage id="campaigns.form.item.select-items" />
            </StyledButton>
          </FooterGroup>
        </Footer>
      }
    >
      {isLoadingMenuItems ? (
        <Loading />
      ) : (
        <DiscountedItemsModalContent
          discountedItems={discountedItems}
          discountedItemCategories={categoriesToDisplay}
          selectedItemIds={selectedItemIds}
          selectedCategoryIds={selectedCategoryIds}
          onSelectionChange={onSelectionChange}
          onFilterChange={onFilterChange}
        />
      )}
    </Modal>
  );
};
